import type { SupportedLanguage } from '@web-store-platforms/types';

type NestedRecord = Record<string, unknown>;

function isObject(value: unknown): value is NestedRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Recursively merges override values into a base object, preserving nested structure.
 *
 * @example
 * const merged = deepMerge({ a: { b: 1 } }, { a: { c: 2 } }); // { a: { b: 1, c: 2 } }
 */
export const deepMerge = (base: NestedRecord, overrides: NestedRecord): NestedRecord => {
    const result: NestedRecord = { ...base };

    for (const key of Object.keys(overrides)) {
        if (isObject(result[key]) && isObject(overrides[key])) {
            result[key] = deepMerge(result[key] as NestedRecord, overrides[key] as NestedRecord);
        } else {
            result[key] = overrides[key];
        }
    }

    return result;
};

export const DEFAULT_LOCALE = 'en';

export const GEO_COUNTRY_COOKIE_NAME = 'geo_country';

export const GEO_COOKIE_TTL = 3600;

const LANGUAGE_EXPLICITLY_SET_KEY = 'languageExplicitlySet';

const DEBUG_LANGUAGES_KEY = 'DEBUG_LANGUAGES';

function isLanguageDebugEnabled(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        return localStorage.getItem(DEBUG_LANGUAGES_KEY) === 'true';
    } catch {
        return false;
    }
}

function debugLanguage(label: string, data: Record<string, unknown>): void {
    if (!isLanguageDebugEnabled()) return;
    console.log(`[DEBUG_LANGUAGES] ${label}`, data);
}

/**
 * Extracts the visitor's country code from CDN/proxy geo headers.
 * Checks a `?geo_country` query-param override first (non-production only),
 * then Cloudflare, Vercel, CloudFront, and generic headers in priority order.
 *
 * @example
 * const country = extractGeoCountryFromHeaders(request.headers, request.nextUrl.searchParams);
 */
export function extractGeoCountryFromHeaders(
    headers: { get(name: string): string | null },
    searchParams?: { get(name: string): string | null },
): string | null {
    if (searchParams && process.env.NODE_ENV !== 'production') {
        const override = searchParams.get('geo_country');
        if (override) return override;
    }

    return (
        headers.get('cf-ipcountry') ??
        headers.get('x-vercel-ip-country') ??
        headers.get('cloudfront-viewer-country') ??
        headers.get('x-country-code') ??
        null
    );
}

interface IResolveLanguageOptions {
    savedLanguage: string | null;
    isExplicitlySelected: boolean;
    geoCountryCode: string | null;
    bootLocale: string | null;
    supportedLanguages: SupportedLanguage[];
}

/**
 * Unified language resolution used by both store and portal.
 * Priority (V1-aligned): localStorage first, then boot, then default,
 * with geo overriding saved language when the user hasn't explicitly chosen one.
 *
 * @example
 * const language = resolveLanguage({
 *   savedLanguage: 'en',
 *   isExplicitlySelected: false,
 *   geoCountryCode: 'AM',
 *   bootLocale: null,
 *   supportedLanguages: [{ isoCode: 'hy', language: 'Armenian', country: 'AM' }],
 * });
 */
export function resolveLanguage(options: IResolveLanguageOptions): string {
    const {
        savedLanguage,
        isExplicitlySelected,
        geoCountryCode,
        bootLocale,
        supportedLanguages,
    } = options;

    debugLanguage('resolveLanguage:input', {
        savedLanguage,
        isExplicitlySelected,
        geoCountryCode,
        bootLocale,
        supportedLanguages: supportedLanguages.map((l) => l.isoCode),
    });

    const hasSupportedList = supportedLanguages.length > 0;
    const supportedIsoCodes = supportedLanguages.map((l) => l.isoCode);
    const isSupported = (code: string | null): boolean =>
        code !== null && (!hasSupportedList || supportedIsoCodes.includes(code));

    const resolve = (): { language: string; branch: string } => {
        if (savedLanguage && isExplicitlySelected && isSupported(savedLanguage)) {
            return { language: savedLanguage, branch: 'explicit-selection' };
        }

        const geoLanguage = geoCountryCode
            ? getLanguageByCountry(geoCountryCode, supportedLanguages)
            : null;

        if (savedLanguage && !isExplicitlySelected && geoLanguage && geoLanguage !== savedLanguage) {
            return { language: geoLanguage, branch: 'geo-override' };
        }

        if (savedLanguage && isSupported(savedLanguage)) {
            return { language: savedLanguage, branch: 'saved-language' };
        }

        if (geoLanguage && isSupported(geoLanguage)) {
            return { language: geoLanguage, branch: 'geo-language' };
        }

        if (bootLocale && isSupported(bootLocale)) {
            return { language: bootLocale, branch: 'boot-locale' };
        }

        return { language: DEFAULT_LOCALE, branch: 'default' };
    };

    const { language, branch } = resolve();
    debugLanguage('resolveLanguage:result', { language, branch });
    return language;
}

/**
 * Maps a country code to the matching language ISO code from the supported list.
 *
 * @example
 * const lang = getLanguageByCountry('FR', supportedLanguages); // 'fr'
 */
export function getLanguageByCountry(
    countryCode: string,
    supportedLanguages: SupportedLanguage[]
): string | null {
    const upperCode = countryCode.toUpperCase();
    const match = supportedLanguages.find(
        (lang) => lang.country.toUpperCase() === upperCode
    );
    return match?.isoCode ?? null;
}

/**
 * Reads the geo_country cookie set by middleware to determine the visitor's country.
 * Returns null in SSR or when the cookie is absent.
 *
 * @example
 * const country = readGeoCookie(); // 'US' | null
 */
export function readGeoCookie(): string | null {
    if (typeof document === 'undefined') return null;

    const match = document.cookie.match(
        new RegExp(`(?:^|;\\s*)${GEO_COUNTRY_COOKIE_NAME}=([^;]+)`)
    );
    const value = match?.[1] ?? null;
    debugLanguage('readGeoCookie', { value });
    return value;
}

/**
 * Checks whether the user has manually selected a language via the language picker.
 * Returns false in SSR or when localStorage is unavailable.
 *
 * @example
 * const explicit = readIsLanguageExplicitlySet(); // true | false
 */
export function readIsLanguageExplicitlySet(): boolean {
    if (typeof window === 'undefined') return false;

    try {
        const result = localStorage.getItem(LANGUAGE_EXPLICITLY_SET_KEY) === 'true';
        debugLanguage('readIsLanguageExplicitlySet', { result });
        return result;
    } catch {
        return false;
    }
}

/**
 * Marks that the user has explicitly chosen a language, preventing geo-based overrides.
 *
 * @example
 * setLanguageExplicitlySelected();
 */
export function setLanguageExplicitlySelected(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.setItem(LANGUAGE_EXPLICITLY_SET_KEY, 'true');
    } catch (error) {
        console.warn('Failed to set language explicit selection flag', error);
    }
}

/**
 * Clears the explicit language selection flag, allowing geo-based detection to take effect again.
 *
 * @example
 * clearLanguageExplicitlySelected();
 */
export function clearLanguageExplicitlySelected(): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(LANGUAGE_EXPLICITLY_SET_KEY);
    } catch (error) {
        console.warn('Failed to clear language explicit selection flag', error);
    }
}
